import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { NavCounts, NavCountsValue } from "./NavCountsContext";

export type { NavCounts, ClientNavCounts, ConsultantNavCounts, AuditorNavCounts, NavCountsValue } from "./NavCountsContext";
export { NavCountsContext, useNavCountsContext } from "./NavCountsContext";

const POLL_MS = 60 * 1000;
/** A navigation-triggered refresh fires at most this often. */
const NAV_REFRESH_MIN_MS = 15 * 1000;

function isKnownRole(value: unknown): value is NavCounts {
  if (!value || typeof value !== "object") return false;
  const role = (value as { role?: unknown }).role;
  return role === "client" || role === "consultant" || role === "auditor";
}

/**
 * Polls GET /nav/counts every 60 s and whenever the window regains focus.
 * 402 / 403 (tier or permission) clear the counts to null; a transient network
 * error keeps the last good value so badges do not flicker.
 *
 * `scopeKey` identifies what the counts belong to (the shell, or the client an
 * organisation view is scoped to); changing it fetches again immediately.
 */
export function useNavCounts(scopeKey: string = "default", enabled: boolean = true): NavCountsPoller {
  const [counts, setCounts] = useState<NavCounts | null>(null);
  const inFlight = useRef(false);
  const lastLoadedAt = useRef(0);
  const generation = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    const gen = generation.current;
    try {
      const data = await api.get<unknown>("/nav/counts");
      if (gen !== generation.current) return;
      setCounts(isKnownRole(data) ? data : null);
      lastLoadedAt.current = Date.now();
    } catch (e) {
      if (gen !== generation.current) return;
      if (e instanceof ApiError && (e.status === 402 || e.status === 403 || e.status === 404)) {
        setCounts(null);
      }
      // Anything else (network, 5xx): keep the last good value.
    } finally {
      inFlight.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setCounts(null);
    if (!enabled) return;

    void load();
    const interval = window.setInterval(() => { void load(); }, POLL_MS);
    const onFocus = () => { void load(); };
    const onVisible = () => { if (document.visibilityState === "visible") void load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, scopeKey, enabled]);

  const refresh = useCallback(() => { void load(); }, [load]);

  /** Cheap refresh after a navigation, throttled so browsing does not hammer the API. */
  const refreshIfStale = useCallback(() => {
    if (Date.now() - lastLoadedAt.current >= NAV_REFRESH_MIN_MS) void load();
  }, [load]);

  return { counts, refresh, refreshIfStale };
}

export interface NavCountsPoller extends NavCountsValue {
  /** Refresh only when the last load is older than 15 s (called on navigation). */
  refreshIfStale: () => void;
}
